import {
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  Inject,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { AccountService } from './../../services/account.service';
import { SocialAuthService } from '@abacritt/angularx-social-login';
import { TranslateService } from '@ngx-translate/core';

interface MenuItem {
  link: string;
  icon: string;
  labelEn: string;
  labelBn: string;
  exact?: boolean;
  withUid?: boolean;
}

@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
})
export class SideMenuComponent implements OnInit, OnChanges, OnDestroy {
  @HostBinding('class.collapse') collapseClass = true;
  @HostBinding('class.in') get inClass() {
    return this.isCollapsed;
  }
  @HostBinding('class.mobile-menu-open') get mobileMenuOpenClass() {
    return this.isMobile && this.isMobileMenuOpen;
  }

  @Input() isCollapsed = false;
  @Output() toggleCollapse = new EventEmitter<boolean>();

  menuItems: MenuItem[] = [
    { link: '/dashboard', icon: 'assets/icons/Home_light.svg', labelEn: 'Dashboard', labelBn: 'ড্যাশবোর্ড', exact: true, withUid: true },
    { link: '/research-portal/stocks/overview', icon: 'assets/icons/Chart_pin_light.svg', labelEn: 'Research Portal', labelBn: 'রিসার্চ পোর্টাল' },
    { link: '/products/equity', icon: 'assets/icons/Line_up_light.svg', labelEn: 'Equities', labelBn: 'ইকুইটি' },
    { link: '/products/fixed-income', icon: 'assets/icons/Widget_alt_light.svg', labelEn: 'Fixed Income', labelBn: 'ফিক্সড ইনকাম' },
    { link: '/dashboard/v-stock-ver2', icon: '/assets/images/icons/stock-picker.png', labelEn: 'VSTOCK', labelBn: 'VSTOCK' },
    { link: '/market-insights', icon: 'assets/icons/Lightbulb_light.svg', labelEn: 'Market Insights', labelBn: 'মার্কেট ইনসাইটস' },
  ];

  currentUser: any;
  isMobile: boolean = false;
  isMobileMenuOpen = false;
  private mutationObserver?: MutationObserver;
  private resizeListener?: () => void;
  private resizeTimeout?: any;
  // isResearchAccess: boolean;
  userInProgress = true;
  isSuperAdmin;
  viewEditprdSignups;
  investorCode;
  isCustomer;
  private langChangeSubscription: Subscription;
  constructor(
    public accountService: AccountService,
    public router: Router,
    private socialAuthService: SocialAuthService,
    public translate: TranslateService,
    @Inject(DOCUMENT) public document: Document,
    private cdRef: ChangeDetectorRef
  ) {
    this.accountService.emitprofileUpdate.subscribe(async () => {
      this.currentUser = await this.accountService.currentUserAsync();
    });
  }

  async ngOnInit() {
    try {
      this.userInProgress = true;
      this.currentUser = await this.accountService.currentUserAsync();
      this.investorCode = this.currentUser?.investorCode;
      this.checkRoles();
    } catch (e) {
      // Handle error if needed
    } finally {
      this.userInProgress = false;
    }

    // Check if mobile screen
    this.checkMobile();

    // Debounced resize handler
    this.resizeListener = () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.checkMobile();
        // Close menu if switching to desktop
        if (!this.isMobile && this.isMobileMenuOpen) {
          this.closeMobileMenuIfNeeded();
        }
      }, 150);
    };
    window.addEventListener('resize', this.resizeListener);

    // Watch for mobile-menu-open class changes on body
    this.isMobileMenuOpen = this.document.body.classList.contains('mobile-menu-open');

    this.mutationObserver = new MutationObserver(() => {
      const wasOpen = this.isMobileMenuOpen;
      this.isMobileMenuOpen = this.document.body.classList.contains('mobile-menu-open');
      // Only trigger change detection if state actually changed
      if (wasOpen !== this.isMobileMenuOpen) {
        this.cdRef.detectChanges();
      }
    });

    this.mutationObserver.observe(this.document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });

    // Subscribe to language changes to update translations
    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.cdRef.detectChanges();
    });
  }

  label(item: MenuItem): string {
    return this.translate.currentLang === 'en' ? item.labelEn : item.labelBn;
  }

  get toggleLabel(): string {
    const en = this.translate.currentLang === 'en';
    if (this.isMobile) return en ? 'Close menu' : 'মেনু বন্ধ করুন';
    if (this.isCollapsed) return en ? 'Expand menu' : 'মেনু প্রসারিত করুন';
    return en ? 'Collapse menu' : 'মেনু সংকুচিত করুন';
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.closeMobileMenuIfNeeded();
  }

  checkMobile() {
    this.isMobile = window.innerWidth <= 767.98;
  }

  closeMobileMenuIfNeeded() {
    if (this.isMobile && this.document.body.classList.contains('mobile-menu-open')) {
      this.isMobileMenuOpen = false;
      this.document.body.classList.remove('mobile-menu-open');
      this.document.body.style.overflow = '';
      this.cdRef.detectChanges();
    }
  }
  checkRoles() {
    this.isSuperAdmin = this.currentUser.priviledges.some((priviledge) => priviledge === 'ADMIN_OPTIONS');
    this.isCustomer = this.currentUser.priviledges.some((priviledge) => priviledge === 'PRODUCT_SIGNUP');
    // this.isResearchAccess = this.currentUser.priviledges.some(
    //   (priviledge) =>
    //     priviledge === 'RESEARCH_PORTAL_FULL_ACCESS' ||
    //     priviledge === 'RESEARCH_PORTAL_PARTIAL_ACCESS' ||
    //     priviledge === 'VIEW_RESEARCH_DATA' ||
    //     priviledge === 'VIEW_LIVE_MARKET_DATA'
    // );

    this.viewEditprdSignups = this.currentUser.priviledges.some((priviledge) => priviledge === 'MONITOR_PRODUCT_SIGNUPS') || false;
    // this.isResearchAdmin =
    //   this.currentUser.roles.some((role) => role === 'ROLE_RESEARCH_ADMIN') ||
    //   this.currentUser.priviledges.some((priviledge) => priviledge === 'UPLOAD_REPORTS');

    // this.isDocumentOfficer = this.currentUser.priviledges.some(
    //   (priviledge) => priviledge === 'REVIEW_PRODUCT_SIGNUP' || priviledge === 'MANAGE_ADMIN_USERS'
    // );

    // this.isCRMXAcess = this.currentUser.priviledges.some(
    //   (priviledges) => priviledges === 'CRM_X_VIEW_ONLY' || priviledges === 'CREATE_CRM_X_USER' || priviledges === 'MANAGE_CORP_FIN_USERS'
    // );
    // this.isCreateProductPriviledges = this.currentUser.priviledges.some((priviledge) => priviledge === 'CREATE_PRODUCT');
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes['isCollapsed']) {
      // Sync body class when isCollapsed changes from parent
      if (this.isCollapsed) {
        this.document.body.classList.add('sidebar-collapse-in');
      } else {
        this.document.body.classList.remove('sidebar-collapse-in');
      }
    }
  }

  resetDashboard(e: Event) {
    e.preventDefault();
    // Add reset logic here
  }

  resetResearchDashboard(e: Event) {
    if (this.currentUser?.priviledges?.some((p) => p === 'RESEARCH_PORTAL_FULL_ACCESS')) {
      this.router.navigate(['research-portal/research/research-reports']);
    } else {
      this.router.navigate(['research-portal']);
    }
    this.accountService.resetDashboard('all');
  }

  onToggleCollapse() {
    // On mobile, close the menu instead of collapsing
    if (this.isMobile && this.isMobileMenuOpen) {
      this.closeMobileMenuIfNeeded();
      return;
    }

    const newState = !this.isCollapsed;
    // Toggle the body class to trigger CSS changes
    if (newState) {
      this.document.body.classList.add('sidebar-collapse-in');
    } else {
      this.document.body.classList.remove('sidebar-collapse-in');
    }
    this.toggleCollapse.emit(newState);
    this.accountService.emit(newState);
  }

  async logout() {
    if (this.socialAuthService) {
      this.socialAuthService.signOut();
    }
    await this.accountService.signOut();
    if (this.currentUser?.roles?.some((role) => role === 'ROLE_CUSTOMER')) {
      this.router.navigate(['/signin']);
    } else {
      this.router.navigate(['/admin-login']);
    }
  }

  ngOnDestroy() {
    // Unsubscribe from language changes
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
    // Clear resize timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    // Remove resize listener
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
    // Disconnect mutation observer
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }
}
